// components/models/MireloSfxControlPanel.tsx
import React, { useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import VideoInputRangeControls from './VideoInputRangeControls';
import { MireloSfxParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

const defaultMireloSfxParams: MireloSfxParams = {
  prompt: '',
  video_input_start_ms: null,
  video_input_end_ms: null,
  seed: -1,
};

export const getDefaultMireloSfxParams = (): MireloSfxParams => ({ ...defaultMireloSfxParams });

const MireloSfxControlPanel: React.FC = React.memo(() => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as MireloSfxParams | undefined
  );

  const updateParam = useCallback((name: keyof MireloSfxParams, value: string | number | null) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams((previous) => {
      const current = previous[diffusionModel];
      if (!current) {
        console.error(`[MireloSfxControlPanel] Missing params for model "${diffusionModel}".`);
        return previous;
      }
      return {
        ...previous,
        [diffusionModel]: {
          ...current,
          [name]: value,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' ? { value_length: String(value).length } : { value }),
      model_name: diffusionModel,
      source_component: 'MireloSfxControlPanel',
    });
  }, [kernel]);

  const handlePromptChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateParam('prompt', event.target.value);
  }, [updateParam]);

  const handleVideoInputRangeChange = useCallback((
    name: 'video_input_start_ms' | 'video_input_end_ms',
    value: number | null,
  ) => {
    updateParam(name, value);
  }, [updateParam]);

  if (!params) return null;

  return (
    <div className="space-y-4">
      <AutoGrowingTextarea
        label="Sound direction (Optional)"
        placeholder="Describe the Foley, ambience, impacts, and off-screen sounds to generate..."
        name="prompt"
        value={params.prompt}
        onChange={handlePromptChange}
        onEnterPress={kernel.current?.triggerGenerate}
        maxHeight={600}
        labelClassName="mb-2 block text-lg font-semibold text-white"
      />
      <VideoInputRangeControls
        startMs={params.video_input_start_ms}
        endMs={params.video_input_end_ms}
        onChange={handleVideoInputRangeChange}
      />
      <p className="text-xs leading-relaxed text-white/45">
        The source clip and SFX duration come from this timeline range, or from the selected video track when the range is blank. Mirelo supports clips from 1 to 60 seconds. The generated soundtrack is separated automatically and placed at the source range start.
      </p>
    </div>
  );
});

MireloSfxControlPanel.displayName = 'MireloSfxControlPanel';

export default MireloSfxControlPanel;
